import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

export interface HudNotification {
  text: string;
  type: 'info' | 'success' | 'error';
}

@Component({
  selector: 'child-table-pagination',
  templateUrl: './child-table-pagination.component.html',
  styleUrls: ['./child-table-pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChildTablePaginationComponent implements OnChanges, OnDestroy {
  @Input() currentPage = 0;
  @Input() totalPages = 1;
  @Input() paginationStart = 0;
  @Input() paginationEnd = 0;
  @Input() totalRecords = 0;

  @Input() editable = true;
  @Input() readonly = false;
  @Input() hasUnsavedChanges = false;
  @Input() lastSaved: Date | null = null;
  @Input() saving = false;

  @Output() prev = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();
  @Output() manualSave = new EventEmitter<void>();

  notification: HudNotification | null = null;
  notificationVisible = false;
  private notificationTimer: ReturnType<typeof setTimeout> | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Auto-clear an active error once there are no more unsaved changes
    // (i.e. a successful save just completed)
    if (
      changes['hasUnsavedChanges'] &&
      !changes['hasUnsavedChanges'].currentValue &&
      this.notification?.type === 'error'
    ) {
      this.dismissNotification();
    }
  }

  ngOnDestroy(): void {
    if (this.notificationTimer) clearTimeout(this.notificationTimer);
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
  }

  showNotification(notification: HudNotification, durationMs = 2500): void {
    if (this.notificationTimer) clearTimeout(this.notificationTimer);
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
    this.notification = notification;
    this.notificationVisible = true;
    this.cdr.markForCheck();

    if (notification.type !== 'error') {
      this.notificationTimer = setTimeout(() => this.dismissNotification(), durationMs);
    }
  }

  dismissNotification(): void {
    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer);
      this.notificationTimer = null;
    }
    this.notificationVisible = false;
    this.cdr.markForCheck();
    this.fadeTimer = setTimeout(() => {
      this.notification = null;
      this.fadeTimer = null;
      this.cdr.markForCheck();
    }, 200);
  }

  get lastSavedText(): string {
    if (!this.lastSaved) return '';
    return this.lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
